import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { ClubFull, HistoryItem, Officer } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useToast } from "@/components/feature/ToastProvider";
import { mediaUrl } from "@/lib/media";

function StatItem({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
      <div className="font-heading text-2xl md:text-3xl font-bold text-primary-700">
        {value}
        {suffix && <span className="ml-0.5 text-base font-semibold">{suffix}</span>}
      </div>
      <div className="mt-1 text-sm text-foreground-500">{label}</div>
    </div>
  );
}

function Timeline({ items }: { items: HistoryItem[] }) {
  return (
    <ol className="relative space-y-8 pl-6 md:pl-8">
      <span className="absolute left-[5px] top-2 bottom-2 w-px bg-background-300" aria-hidden="true"></span>
      {items.map((item, i) => (
        <li key={`${item.year}-${i}`} className="relative">
          <span
            className="absolute -left-[23px] top-1.5 w-3 h-3 rounded-full border-2 border-background-50 bg-secondary-500 md:-left-[31px]"
            aria-hidden="true"
          ></span>
          <div className="flex flex-wrap items-center gap-2">
            {item.year && (
              <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-bold text-primary-700">
                {item.year}
              </span>
            )}
            {item.date && (
              <span className="text-xs font-semibold text-secondary-800">{item.date}</span>
            )}
          </div>
          <p className="mt-1.5 text-sm md:text-base leading-relaxed text-foreground-700">
            {item.content}
          </p>
        </li>
      ))}
    </ol>
  );
}

function OfficerCard({ officer }: { officer: Officer }) {
  const initial = (officer.name || "?").trim().slice(0, 1);
  return (
    <article className="rounded-xl border border-background-200 bg-background-50 p-4 text-center">
      <div className="mx-auto w-24 h-24 rounded-full overflow-hidden bg-background-100 border border-background-200 flex items-center justify-center">
        {officer.photo ? (
          <img
            src={mediaUrl(officer.photo)}
            alt={`${officer.name} 임원 사진`}
            className="h-full w-full object-cover object-top"
          />
        ) : (
          <span className="font-heading text-2xl font-bold text-primary-600">{initial}</span>
        )}
      </div>
      <h3 className="mt-4 font-heading font-bold text-foreground-950">
        {officer.name}
        {officer.aho && (
          <span className="ml-1 text-sm font-medium text-foreground-500">({officer.aho})</span>
        )}
      </h3>
      {officer.role && (
        <span className="mt-2 inline-block rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
          {officer.role}
        </span>
      )}
      {officer.biz_name && (
        <p className="mt-2 text-xs text-foreground-500">{officer.biz_name}</p>
      )}
    </article>
  );
}

export default function About() {
  const { toast } = useToast();
  const { data, loading, error, reload } = useApi<ClubFull>("club.php");

  const history = data?.history ?? [];
  const officers = data?.officers ?? [];
  const activities = data?.activities ?? [];
  const bank = data?.bank;
  const stats = data?.stats;

  const copyAccount = (text: string) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => toast("계좌번호를 복사했습니다.", "success"))
        .catch(() => toast("복사에 실패했습니다.", "error"));
    } else {
      toast("복사를 지원하지 않는 환경입니다.", "error");
    }
  };

  return (
    <>
      <PageBanner
        title="클럽소개"
        description="대전민들레로타리클럽의 걸어온 길과 사람들을 소개합니다."
        breadcrumb={[{ label: "클럽소개" }]}
      />

      {loading ? (
        <section className="mx-auto max-w-4xl px-4 md:px-6 py-12 space-y-8">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </section>
      ) : error ? (
        <section className="mx-auto max-w-4xl px-4 md:px-6 py-12">
          <EmptyState
            icon="ri-error-warning-line"
            title="클럽 정보를 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      ) : (
        <>
          {/* 1) 헤더 */}
          <section className="w-full border-b border-background-200 bg-background-50">
            <div className="mx-auto max-w-4xl px-4 md:px-6 py-10 md:py-12 text-center">
              <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-secondary-500 text-primary-950">
                <i className="ri-seedling-fill text-2xl" aria-hidden="true"></i>
              </span>
              <h2 className="mt-5 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {data?.club_name || "대전민들레로타리클럽"}
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                {data?.district || "국제로타리 3680지구"}
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm">
                {data?.found_year && (
                  <span className="rounded-full bg-background-100 px-3.5 py-1.5 font-medium text-foreground-700">
                    Since {data.found_year}
                  </span>
                )}
                {data?.years && (
                  <span className="rounded-full bg-primary-100 px-3.5 py-1.5 font-semibold text-primary-700">
                    {data.years}주년
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* 2) 인사말 */}
          {data?.greeting && (
            <section className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
              <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs font-semibold text-secondary-900">
                <i className="ri-chat-quote-line" aria-hidden="true"></i>
                인사말
              </span>
              <div className="mt-6 whitespace-pre-line break-words text-sm md:text-base leading-loose text-foreground-700">
                {data.greeting}
              </div>
              {data.greeting_name && (
                <p className="mt-6 text-right font-heading font-bold text-foreground-950">
                  {data.greeting_name}
                </p>
              )}
            </section>
          )}

          {/* 3) 통계 스트립 */}
          {stats && (stats.member || stats.photo || stats.years) && (
            <section className="w-full bg-background-100">
              <div className="mx-auto max-w-4xl px-4 md:px-6 py-10">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {typeof stats.member === "number" && (
                    <StatItem label="재적회원" value={String(stats.member)} suffix="명" />
                  )}
                  {typeof stats.photo === "number" && (
                    <StatItem label="활동사진" value={String(stats.photo)} suffix="장" />
                  )}
                  {stats.years != null && (
                    <StatItem label="창립주년" value={String(stats.years)} suffix="주년" />
                  )}
                </div>
              </div>
            </section>
          )}

          {/* 4) 주요 연혁 */}
          {history.length > 0 && (
            <section className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                주요 연혁
              </h3>
              <Timeline items={history} />
            </section>
          )}

          {/* 5) 지속 활동 */}
          {activities.length > 0 && (
            <section className="w-full bg-background-100">
              <div className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  지속 활동
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activities.map((a, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                    >
                      <span className="mt-0.5 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-check-line text-sm" aria-hidden="true"></i>
                      </span>
                      <span className="text-sm leading-relaxed text-foreground-700">{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* 6) 임원 */}
          {officers.length > 0 && (
            <section className="mx-auto max-w-6xl px-4 md:px-6 py-12 md:py-16">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                임원
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {officers.map((o, i) => (
                  <OfficerCard key={`${o.name}-${i}`} officer={o} />
                ))}
              </div>
            </section>
          )}

          {/* 7) 클럽 계좌 */}
          {bank && (bank.name || bank.account) && (
            <section className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
              <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-6 md:p-8">
                <div className="flex items-center gap-2 text-secondary-900">
                  <i className="ri-bank-card-line text-xl" aria-hidden="true"></i>
                  <h3 className="font-heading text-lg font-bold">클럽 계좌</h3>
                </div>
                <p className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  {[bank.name, bank.account].filter(Boolean).join(" ")}
                </p>
                {bank.holder && (
                  <p className="mt-1 text-sm text-foreground-600">예금주 {bank.holder}</p>
                )}
                <button
                  type="button"
                  onClick={() =>
                    copyAccount([bank.name, bank.account].filter(Boolean).join(" "))
                  }
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-secondary-500 px-5 py-2.5 text-sm font-semibold text-primary-950 hover:bg-secondary-600 transition-colors"
                >
                  <i className="ri-file-copy-line" aria-hidden="true"></i>
                  계좌번호 복사
                </button>
              </div>
            </section>
          )}

          {/* 8) 연락처 */}
          {(data?.tel || data?.email || data?.addr) && (
            <section className="w-full bg-background-100">
              <div className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  연락처
                </h3>
                <ul className="space-y-3">
                  {data?.tel && (
                    <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-phone-line" aria-hidden="true"></i>
                      </span>
                      <a
                        href={`tel:${data.tel.replace(/[^0-9+]/g, "")}`}
                        className="text-sm font-medium text-foreground-800 hover:text-primary-700"
                      >
                        {data.tel}
                      </a>
                    </li>
                  )}
                  {data?.email && (
                    <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-mail-line" aria-hidden="true"></i>
                      </span>
                      <a
                        href={`mailto:${data.email}`}
                        className="text-sm font-medium text-foreground-800 hover:text-primary-700 break-all"
                      >
                        {data.email}
                      </a>
                    </li>
                  )}
                  {data?.addr && (
                    <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-map-pin-line" aria-hidden="true"></i>
                      </span>
                      <span className="text-sm font-medium text-foreground-800">{data.addr}</span>
                    </li>
                  )}
                </ul>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}